import { Controller } from '@hotwired/stimulus';

export default class extends Controller {
  static targets = ['row', 'header', 'indicator', 'container', 'loadMoreButton'];
  static values = {
    pageSize: { type: Number, default: 20 },
    totalRows: { type: Number, default: 0 },
  };

  currentSortColumn = null;
  currentSortDirection = null;
  visibleCount = 0;

  connect() {
    this.headerTargets.forEach((header) => {
      header.addEventListener('click', this.handleSortClick.bind(this));
    });
    this.visibleCount = this.pageSizeValue;
    this.updateRowVisibility();
  }

  updateRowVisibility() {
    this.rowTargets.forEach((row, index) => {
      row.style.display = index < this.visibleCount ? '' : 'none';
    });
    this.updateLoadMoreButton();
  }

  updateLoadMoreButton() {
    if (this.hasLoadMoreButtonTarget) {
      const allVisible = this.visibleCount >= this.rowTargets.length;
      this.loadMoreButtonTarget.classList.toggle('hidden', allVisible);
    }
  }

  loadMore() {
    this.visibleCount = Math.min(this.visibleCount + this.pageSizeValue, this.rowTargets.length);
    this.updateRowVisibility();
  }

  parseTimeAgo(timeStr) {
    if (!timeStr) return Infinity;
    const str = timeStr.toLowerCase();
    if (str.includes('just now')) return 0;
    const match = str.match(/(\d+)\s*(hour|day|hr|min)/);
    if (!match) return Infinity;
    const value = parseInt(match[1], 10);
    const unit = match[2];
    if (unit.startsWith('min')) return value;
    if (unit.startsWith('hour') || unit === 'hr') return value * 60;
    if (unit.startsWith('day')) return value * 60 * 24;
    return Infinity;
  }

  parseTimeTaken(timeStr) {
    if (!timeStr) return 0;
    let minutes = 0;
    const hrMatch = timeStr.match(/(\d+)\s*hr/);
    const minMatch = timeStr.match(/(\d+)\s*min/);
    if (hrMatch) minutes += parseInt(hrMatch[1], 10) * 60;
    if (minMatch) minutes += parseInt(minMatch[1], 10);
    return minutes;
  }

  getSortValue(row, column) {
    switch (column) {
      case 'name':
        return (row.getAttribute('data-name') || '').toLowerCase();
      case 'score':
        return parseInt(row.getAttribute('data-score') || '0', 10);
      case 'completed':
        return this.parseTimeAgo(row.getAttribute('data-completed-time') || '');
      case 'time':
        return this.parseTimeTaken(row.getAttribute('data-time-taken') || '');
      case 'goals':
        return parseInt(row.getAttribute('data-goals-completed') || '0', 10);
      case 'offers':
        return parseInt(row.getAttribute('data-offer-links-clicked') || '0', 10);
      default:
        return '';
    }
  }

  sortTable(column, direction) {
    const rowsArray = Array.from(this.rowTargets);
    const container = this.containerTarget;
    if (!container) return;

    rowsArray.sort((a, b) => {
      const aVal = this.getSortValue(a, column);
      const bVal = this.getSortValue(b, column);

      let comparison = 0;
      if (aVal < bVal) {
        comparison = -1;
      } else if (aVal > bVal) {
        comparison = 1;
      }

      return direction === 'asc' ? comparison : -comparison;
    });

    rowsArray.forEach((row) => {
      container.appendChild(row);
    });

    this.updateRowVisibility();
  }

  updateSortIndicators(column, direction) {
    this.headerTargets.forEach((header) => {
      const indicator = header.querySelector('.sort-indicator');
      const headerColumn = header.getAttribute('data-sort-column');

      if (headerColumn === column && indicator) {
        indicator.style.display = 'inline-flex';
        const icon = indicator.querySelector('svg');
        if (icon) {
          icon.style.transform = direction === 'desc' ? 'rotate(180deg)' : '';
        }
      } else if (indicator) {
        indicator.style.display = 'none';
      }
    });
  }

  handleSortClick(event) {
    const header = event.currentTarget;
    const column = header.getAttribute('data-sort-column');

    if (this.currentSortColumn === column && this.currentSortDirection === 'asc') {
      this.currentSortDirection = 'desc';
    } else {
      this.currentSortDirection = 'asc';
    }

    this.currentSortColumn = column;
    this.sortTable(column, this.currentSortDirection);
    this.updateSortIndicators(column, this.currentSortDirection);
  }
}
